import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { API_BASE, api, apiError, formatDate, formatDateTime } from "@/lib/api";
import { Seo } from "@/components/Seo";
import {
  AdminBanner,
  AdminButton,
  AdminCard,
  AdminPageHead,
  AdminSpinner,
  AdminTable,
  Drawer,
  EmptyState,
  FilterTabs,
  StatusBadge,
} from "@/components/admin/AdminUi";

/* ------------------------------------------------------------ Testimonials */

const T_TABS = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "under_review", label: "Under Review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "archived", label: "Archived" },
];

export function AdminTestimonials() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [banner, setBanner] = useState({ status: "idle", message: "" });

  const load = useCallback(() => {
    api
      .get("/admin/testimonials", { params: { status } })
      .then((r) => setData(r.data))
      .catch((e) => {
        setData({ items: [], counts: {} });
        setBanner({ status: "error", message: apiError(e) });
      });
  }, [status]);

  useEffect(load, [load]);

  const setStatusFor = async (id, newStatus) => {
    try {
      await api.patch(`/admin/testimonials/${id}/status`, { status: newStatus });
      setBanner({
        status: "ok",
        message:
          newStatus === "approved"
            ? "Approved. This testimonial is now visible on the public site."
            : `Status updated to "${newStatus.replace(/_/g, " ")}".`,
      });
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  return (
    <div data-testid="cms-testimonials">
      <Seo title="Testimonials | Bahamas Kidney Association CMS" noindex />
      <AdminPageHead
        title="Testimonials"
        subtitle="Submissions are private until approved. Submitter emails are never shown publicly."
      />
      <AdminBanner
        status={banner.status}
        message={banner.message}
        onDismiss={() => setBanner({ status: "idle", message: "" })}
      />
      <FilterTabs
        tabs={T_TABS}
        value={status}
        onChange={setStatus}
        counts={data?.counts || {}}
        testIdPrefix="cms-testimonials-filter"
      />

      <AdminCard className="overflow-hidden">
        {data === null ? (
          <AdminSpinner />
        ) : data.items.length === 0 ? (
          <EmptyState message="No testimonials in this view." testId="cms-testimonials-empty" />
        ) : (
          <AdminTable
            columns={["Name", "Connection", "Testimonial", "Received", "Status", "Actions"]}
            testId="cms-testimonials-table"
          >
            {data.items.map((t) => (
              <tr key={t.id} className="hover:bg-slate-50/70" data-testid={`cms-testimonial-row-${t.id}`}>
                <td className="px-5 py-4 font-bold text-bka-navy">{t.full_name}</td>
                <td className="px-5 py-4 text-slate-500">{t.connection_to_bka || "—"}</td>
                <td className="max-w-xs px-5 py-4 text-slate-500">
                  <span className="line-clamp-2">{t.testimonial}</span>
                </td>
                <td className="px-5 py-4 text-slate-500">{formatDate(t.created_at)}</td>
                <td className="px-5 py-4">
                  <StatusBadge status={t.status} testId={`cms-testimonial-status-${t.id}`} />
                </td>
                <td className="px-5 py-4">
                  <div className="flex flex-wrap gap-1.5">
                    {t.status !== "approved" && (
                      <AdminButton
                        variant="emerald"
                        testId={`cms-testimonial-approve-${t.id}`}
                        onClick={() => setStatusFor(t.id, "approved")}
                        className="px-3 py-1.5 text-xs"
                      >
                        Approve
                      </AdminButton>
                    )}
                    {t.status !== "rejected" && (
                      <AdminButton
                        variant="danger"
                        testId={`cms-testimonial-reject-${t.id}`}
                        onClick={() => setStatusFor(t.id, "rejected")}
                        className="px-3 py-1.5 text-xs"
                      >
                        Reject
                      </AdminButton>
                    )}
                    {t.status !== "archived" && (
                      <AdminButton
                        variant="ghost"
                        testId={`cms-testimonial-archive-${t.id}`}
                        onClick={() => setStatusFor(t.id, "archived")}
                        className="px-3 py-1.5 text-xs"
                      >
                        Archive
                      </AdminButton>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </AdminTable>
        )}
      </AdminCard>
    </div>
  );
}

/* --------------------------------------------------------------- Enquiries */

const E_TABS = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
  { value: "archived", label: "Archived" },
];

const FORM_LABELS = {
  contact: "General Contact",
  support: "Get Support",
  volunteer: "Get Involved",
};

export function AdminEnquiries() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [formType, setFormType] = useState("all");
  const [selected, setSelected] = useState(null);
  const [banner, setBanner] = useState({ status: "idle", message: "" });

  const load = useCallback(() => {
    api
      .get("/admin/enquiries", { params: { status, form_type: formType } })
      .then((r) => setData(r.data))
      .catch((e) => {
        setData({ items: [], counts: {} });
        setBanner({ status: "error", message: apiError(e) });
      });
  }, [status, formType]);

  useEffect(load, [load]);

  const setStatusFor = async (id, newStatus) => {
    try {
      await api.patch(`/admin/enquiries/${id}/status`, { status: newStatus });
      setBanner({ status: "ok", message: `Marked as "${newStatus.replace(/_/g, " ")}".` });
      setSelected(null);
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  return (
    <div data-testid="cms-enquiries">
      <Seo title="Forms & Enquiries | Bahamas Kidney Association CMS" noindex />
      <AdminPageHead title="Forms & Enquiries" subtitle="Every website form submission lands here.">
        <a href={`${API_BASE}/admin/enquiries/export.csv`} data-testid="cms-enquiries-export">
          <AdminButton variant="ghost">
            <Download className="h-4 w-4" /> Export CSV
          </AdminButton>
        </a>
      </AdminPageHead>

      <AdminBanner
        status={banner.status}
        message={banner.message}
        onDismiss={() => setBanner({ status: "idle", message: "" })}
      />

      <FilterTabs
        tabs={E_TABS}
        value={status}
        onChange={setStatus}
        counts={data?.counts || {}}
        testIdPrefix="cms-enquiries-filter"
      />
      <FilterTabs
        tabs={[
          { value: "all", label: "All Forms" },
          { value: "contact", label: "Contact" },
          { value: "support", label: "Get Support" },
          { value: "volunteer", label: "Get Involved" },
        ]}
        value={formType}
        onChange={setFormType}
        testIdPrefix="cms-enquiries-type"
      />

      <AdminCard className="overflow-hidden">
        {data === null ? (
          <AdminSpinner />
        ) : data.items.length === 0 ? (
          <EmptyState message="No enquiries in this view." testId="cms-enquiries-empty" />
        ) : (
          <AdminTable
            columns={["From", "Form", "Subject", "Received", "Status", "Actions"]}
            testId="cms-enquiries-table"
          >
            {data.items.map((e) => (
              <tr key={e.id} className="hover:bg-slate-50/70" data-testid={`cms-enquiry-row-${e.id}`}>
                <td className="px-5 py-4">
                  <p className="font-bold text-bka-navy">{e.name}</p>
                  <p className="text-xs text-slate-500">{e.email}</p>
                </td>
                <td className="px-5 py-4 text-slate-500">{FORM_LABELS[e.form_type] || e.form_type}</td>
                <td className="px-5 py-4 text-slate-500">{e.subject || "—"}</td>
                <td className="px-5 py-4 text-slate-500">{formatDate(e.created_at)}</td>
                <td className="px-5 py-4">
                  <StatusBadge status={e.status} testId={`cms-enquiry-status-${e.id}`} />
                </td>
                <td className="px-5 py-4">
                  <AdminButton
                    variant="ghost"
                    testId={`cms-enquiry-view-${e.id}`}
                    onClick={() => setSelected(e)}
                    className="px-3 py-1.5 text-xs"
                  >
                    Open
                  </AdminButton>
                </td>
              </tr>
            ))}
          </AdminTable>
        )}
      </AdminCard>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title="Enquiry Details"
        testId="cms-enquiry-drawer"
      >
        {selected && (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="font-display text-xl font-bold text-bka-navy">{selected.name}</h3>
                <p className="mt-1 text-sm text-slate-500">
                  {FORM_LABELS[selected.form_type] || selected.form_type}
                </p>
              </div>
              <StatusBadge status={selected.status} />
            </div>

            <div className="flex flex-wrap gap-2">
              {["new", "in_progress", "resolved", "archived"].map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`cms-enquiry-set-${s}`}
                  onClick={() => setStatusFor(selected.id, s)}
                  disabled={selected.status === s}
                  className={`rounded-xl px-3.5 py-2 text-xs font-bold capitalize transition-colors ${
                    selected.status === s
                      ? "cursor-default bg-bka-navy text-white"
                      : "border border-slate-200 text-slate-600 hover:border-bka-emerald hover:text-bka-emerald"
                  }`}
                >
                  {s.replace(/_/g, " ")}
                </button>
              ))}
            </div>

            <dl className="divide-y divide-slate-100 rounded-2xl border border-slate-200 text-sm">
              {Object.entries(selected.payload || {})
                .filter(([k]) => k !== "consent")
                .map(([key, value]) => (
                  <div key={key} className="px-4 py-3">
                    <dt className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      {key.replace(/_/g, " ")}
                    </dt>
                    <dd className="mt-1 whitespace-pre-wrap text-bka-ink">
                      {Array.isArray(value) ? value.join(", ") : String(value ?? "—") || "—"}
                    </dd>
                  </div>
                ))}
              <div className="px-4 py-3">
                <dt className="text-xs font-bold uppercase tracking-wider text-slate-400">Received</dt>
                <dd className="mt-1 text-bka-ink">{formatDateTime(selected.created_at)}</dd>
              </div>
            </dl>

            <a
              href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "Your enquiry")}`}
              data-testid="cms-enquiry-reply"
            >
              <AdminButton variant="emerald">Reply by Email</AdminButton>
            </a>
          </div>
        )}
      </Drawer>
    </div>
  );
}
